import { AiFillHome } from 'react-icons/ai';
import { MdRestaurantMenu, MdOutlineCategory } from 'react-icons/md';
import { FaMoneyBillWave, FaUserFriends, FaGift, FaUserShield, FaCog, FaTags, FaImages, FaTabletAlt } from 'react-icons/fa';
import { GiKnifeFork } from 'react-icons/gi';

export const navItems = [
  'Home',
  'Categories',
  'Product',
  'Sales',
  'Members',
  'Coupon',
  'Banner',
  'Device',
  'Settings',
  'User Management',
  'Restaurant Management',
  'Role Management',
  'Permission Management'
];

export const navIcons = {
  Home: <AiFillHome className="nav-icon" />,
  Categories: <MdOutlineCategory className="nav-icon" />,
  Product: <MdRestaurantMenu className="nav-icon" />,
  Sales: <FaMoneyBillWave className="nav-icon" />,
  Members: <FaUserFriends className="nav-icon" />,
  Coupon: <FaTags className="nav-icon" />,
  Banner: <FaImages className="nav-icon" />,
  Device: <FaTabletAlt className="nav-icon" />,
  Promotion: <FaGift className="nav-icon" />,
  Settings: <FaCog className="nav-icon" />,
  'User Management': <FaUserShield className="nav-icon" />,
  'Restaurant Management': <GiKnifeFork className="nav-icon" />,
  'Role Management': <FaUserShield className="nav-icon" />,
  'Permission Management': <FaUserShield className="nav-icon" />,
};

export const pageToPath = {
  'Home': '/home',
  'Categories': '/categories',
  'Members': '/members',
  'Coupon': '/coupon',
  'Banner': '/banner',
  'Device': '/device',
  'Promotion': '/promotion',
  'Product': '/product',
  'ProductForm': '/product/new',
  'ProductEdit': '/product/edit',
  'ConfigurableProductForm': '/product/new/configurable',
  'ConfigurableProductEdit': '/product/edit/configurable',
  'BundleProductForm': '/product/new/bundle',
  'BundleProductEdit': '/product/edit/bundle',
  'Sales': '/sales',
  'Role Management': '/roles',
  'Permission Management': '/permissions',
  'Settings': '/settings/printer-management',
  'Admin': '/users',
  'Restaurants': '/restaurants',
  'Restaurant Management': '/restaurants',
};

// 权限代码映射
export const permissionCodes = {
  'Home': 'home',
  'Categories': 'catalog:management',
  'Product': 'product',
  'Sales': 'sales',
  'Members': 'members',
  'Coupon': 'coupon',
  'Banner': 'banner',
  'Device': 'device',
  'Settings': 'settings',
  'User Management': 'users',
  'Restaurant Management': 'restaurants',
  'Role Management': 'roles',
  'Permission Management': 'permissions',
};

// 获取缓存的菜单权限
export const getPermissionsMenu = () => {
  try {
    const permissions = localStorage.getItem('permissionsMenu');
    if (permissions) {
      return JSON.parse(permissions);
    }
  } catch (error) {
    console.error('Error parsing permissionsMenu from cache:', error);
  }
  return [];
};

// 缓存菜单权限
export const setPermissionsMenu = (permissions) => {
  try {
    localStorage.setItem('permissionsMenu', JSON.stringify(permissions));
  } catch (error) {
    console.error('Error caching permissionsMenu:', error);
  }
};

// 检查用户是否有某个菜单权限
export const hasMenuPermission = (item) => {
  const permissions = getPermissionsMenu();
  const permissionCode = permissionCodes[item];

  if (!permissionCode) return true;

  return permissions.includes(permissionCode);
};

// 导出 hasPermission 函数（用于兼容性，但标记为已弃用）
// 注意：这个函数检查的是菜单权限，不是功能权限
// 建议使用 hasMenuPermission 以明确意图
export const hasPermission = hasMenuPermission;

// 过滤有权限的导航项
export const getFilteredNavItems = () => {
  return navItems.filter(item => hasMenuPermission(item));
};

// 过滤有权限的导航项和图标
export const getFilteredNavItemsWithIcons = () => {
  const filteredItems = getFilteredNavItems();
  const filteredIcons = {};

  filteredItems.forEach(item => {
    if (navIcons[item]) {
      filteredIcons[item] = navIcons[item];
    }
  });

  return {
    items: filteredItems,
    icons: filteredIcons,
  };
};

// 获取默认首页（根据权限自动选择）
export const getDefaultHomePage = () => {
  const filteredItems = getFilteredNavItems();

  if (filteredItems.length === 0) {
    return '/login';
  }

  // 按照navItems的顺序，找到第一个有权限的页面
  for (const item of navItems) {
    if (filteredItems.includes(item)) {
      return pageToPath[item] || '/home';
    }
  }

  // 如果navItems中没有找到，返回第一个过滤后的页面
  return pageToPath[filteredItems[0]] || '/home';
};

// 检查是否有权限访问特定页面，如果没有则返回下一个有权限的页面
export const getNextAvailablePage = (currentPage) => {
  const filteredItems = getFilteredNavItems();

  // 如果当前页面有权限，直接返回
  if (filteredItems.includes(currentPage)) {
    return currentPage;
  }

  // 如果当前页面没有权限，找到下一个有权限的页面
  const currentIndex = navItems.indexOf(currentPage);

  // 向后查找
  for (let i = currentIndex + 1; i < navItems.length; i++) {
    if (filteredItems.includes(navItems[i])) {
      return navItems[i];
    }
  }

  // 如果向后找不到，向前查找
  for (let i = currentIndex - 1; i >= 0; i--) {
    if (filteredItems.includes(navItems[i])) {
      return navItems[i];
    }
  }

  // 如果还是找不到，返回默认首页
  return getDefaultHomePage();
};

// 处理导航点击，自动跳转到有权限的页面
export const handleNavClick = (item, onNavigate) => {
  if (!onNavigate) return;

  // 检查是否有权限
  if (!hasMenuPermission(item)) {
    console.warn(`No permission to access: ${item}`);
    // 找到下一个有权限的页面
    const nextAvailablePage = getNextAvailablePage(item);
    const route = getRouteFromItem(nextAvailablePage);
    onNavigate(route);
    return;
  }

  const routeMap = {
    'User Management': 'Admin',
    'Restaurant Management': 'Restaurants',
    'Role Management': 'Role Management',
    'Permission Management': 'Permission Management',
  };

  const route = routeMap[item] || item;
  onNavigate(route);
};

// 获取路由，如果没有权限则返回下一个有权限的路由
export const getRouteFromItem = (item) => {
  // 检查是否有权限
  if (!hasMenuPermission(item)) {
    console.warn(`No permission to access: ${item}`);
    // 找到下一个有权限的页面
    const nextAvailablePage = getNextAvailablePage(item);
    return getRouteFromItem(nextAvailablePage);
  }

  const routeMap = {
    'User Management': 'Admin',
    'Restaurant Management': 'Restaurants',
    'Role Management': 'Role Management',
    'Permission Management': 'Permission Management',
  };

  return routeMap[item] || item;
};

// 获取所有有权限的路由路径
export const getAllAvailableRoutes = () => {
  const filteredItems = getFilteredNavItems();
  const routes = {};

  filteredItems.forEach(item => {
    if (pageToPath[item]) {
      routes[item] = pageToPath[item];
    }
  });

  return routes;
};

// 路由守卫函数
export const routeGuard = (targetPage) => {
  // 如果目标页面有权限，直接返回目标页面
  if (hasMenuPermission(targetPage)) {
    return targetPage;
  }

  // 如果没有权限，返回下一个有权限的页面
  return getNextAvailablePage(targetPage);
};

// 使用示例：在应用初始化时设置默认首页
export const initializeApp = () => {
  const defaultHome = getDefaultHomePage();
  console.log('Default home page:', defaultHome);
  return defaultHome;
};

// 批量检查权限
export const checkMultiplePermissions = (items) => {
  return items.filter(item => hasMenuPermission(item));
};

// 获取用户权限统计
export const getPermissionStats = () => {
  const totalItems = navItems.length;
  const availableItems = getFilteredNavItems();
  const availableCount = availableItems.length;
  const permissionRate = (availableCount / totalItems) * 100;

  return {
    total: totalItems,
    available: availableCount,
    rate: permissionRate.toFixed(2),
    availableItems: availableItems,
  };
};

// 新增：根据路径获取页面名称
export const getPageNameByPath = (path) => {
  for (const [page, pagePath] of Object.entries(pageToPath)) {
    if (pagePath === path) {
      return page;
    }
  }
  return null;
};

// 新增：根据页面名称获取路径（增强版）
export const getPathByPageName = (pageName) => {
  // 首先检查直接映射
  if (pageToPath[pageName]) {
    return pageToPath[pageName];
  }

  // 然后检查路由映射
  const routeMap = {
    'User Management': 'Admin',
    'Restaurant Management': 'Restaurants',
    'Role Management': 'Role Management',
    'Permission Management': 'Permission Management',
  };

  const mappedName = routeMap[pageName] || pageName;
  return pageToPath[mappedName] || `/${pageName.toLowerCase().replace(/\s+/g, '-')}`;
};
